<template>
  <v-form ref="form" v-model="formValid">
    <div class="mb-4 text-left">
      <div class="subtitle-1 font-weight-bold mb-4">Taxpayer Type</div>
      <v-radio-group v-model="form.taxpayerType" row class="mt-0" :disabled="identityDisabled">
        <v-radio label="Individual" value="Individual" />
        <v-radio label="Business" value="Business" />
      </v-radio-group>
    </div>

    <!-- Full Legal Name - Individual Only -->
    <v-text-field
      v-if="isIndividualTaxpayer"
      :label="fieldLabel('Full Legal Name', taxpayerFieldsDisabled)"
      v-model="form.fullLegalName"
      :rules="[rules.fullLegalName]"
      :disabled="identityDisabled"
      maxlength="255"
      autocomplete="legal-name"
      name="fullLegalName"
      outlined
      dense
      class="pa-1"
    />
    <div v-else>
      <v-select
        :label="fieldLabel('Tax Classification', taxpayerFieldsDisabled)"
        v-model="form.taxClassification"
        :items="taxQualification"
        item-text="label"
        item-value="value"
        :rules="[rules.taxClassification]"
        :disabled="identityDisabled"
        outlined
        dense
        class="pa-1"
      />
      <div v-if="form.taxClassification">
        <v-text-field
          v-if="form.taxClassification === SOLE_PROPRIETOR_SINGLE_MEMBER_LLC"
          key="legalIndividualName"
          :label="fieldLabel('Legal Individual Name', taxpayerFieldsDisabled)"
          v-model="form.fullLegalName"
          :rules="[rules.fullLegalName]"
          :disabled="identityDisabled"
          maxlength="255"
          autocomplete="legal-name"
          name="legalIndividualName"
          outlined
          dense
          class="pa-1"
        />
        <v-text-field
          key="legalBusinessName"
          :label="fieldLabel('Legal Business Name', taxpayerFieldsDisabled)"
          v-model="form.legalBusinessName"
          :rules="[rules.legalBusinessName]"
          :disabled="identityDisabled"
          maxlength="255"
          autocomplete="organization"
          name="legalBusinessName"
          outlined
          dense
          class="pa-1"
        />
        <v-text-field
          v-if="form.taxClassification !== SOLE_PROPRIETOR_SINGLE_MEMBER_LLC"
          key="tradeName"
          :label="fieldLabel('Trade Name', taxpayerFieldsDisabled)"
          v-model="form.taxTradeName"
          :rules="[rules.tradeName]"
          :disabled="identityDisabled"
          maxlength="255"
          autocomplete="organization"
          name="tradeName"
          outlined
          dense
          class="pa-1"
        />
      </div>
    </div>

    <!-- Case 0: the number is confidential to the user, so a third party (e.g. an admin
         editing someone else) only ever sees whether one is on file. There's no value to
         render, so this is a status banner rather than an input. -->
    <Alert
      v-if="taxIdMode === 'readonly'"
      class="mb-4"
      :variant="hasTaxInfo ? 'success' : 'error'"
      :message="readonlyTaxIdMessage"
      :data-testid="hasTaxInfo ? 'tax-id-saved' : 'tax-id-not-saved'"
    />

    <!-- Case 1: no tax info on file yet, or taxpayer type switched (Individual <-> Business) - input field with eyeball icon -->
    <v-row v-else-if="requiresNewTaxId" class="my-0">
      <v-col cols="12" sm="6" class="py-0">
        <v-text-field
          type="text"
          :label="`${taxIdTypeLabel}*`"
          v-model="formattedTaxId"
          :key="taxIdAbbreviation"
          :placeholder="taxIdPlaceholder"
          :rules="taxIdRules"
          @input="taxIdDirty = true"
          @keypress="onTaxIdKeypress"
          :maxlength="taxIdMaxLength"
          inputmode="numeric"
          autocomplete="one-time-code"
          name="tax-input-field"
          required
          outlined
          dense
          class="pa-1"
        >
          <template v-slot:append>
            <v-icon @click="showTaxId = !showTaxId" tabindex="-1">
              {{ showTaxId ? "mdi-eye" : "mdi-eye-off" }}
            </v-icon>
          </template>
        </v-text-field>
      </v-col>
    </v-row>

    <!-- Case 2: Tax Info already exists - readonly field with edit button -->
    <v-row v-else-if="taxIdMode === 'edit'" class="my-0">
      <v-col cols="12" sm="6" class="py-0">
        <v-text-field
          :label="`${taxIdTypeLabel}*`"
          :value="maskedTaxId"
          disabled
          outlined
          dense
          class="pa-1"
        >
          <template v-slot:append-outer>
            <v-btn icon small @click="openTaxIdEditDialog" class="mt-n1">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </v-col>
    </v-row>

    <!-- Tax Info Edit Dialog -->
    <v-dialog v-if="taxIdMode === 'edit'" v-model="taxIdEditDialog" max-width="560px" persistent>
      <v-card>
        <v-card-title class="text-h6">
          {{ `Edit ${taxIdTypeLabel}` }}
        </v-card-title>
        <v-card-text>
          <div class="mb-4 text-left">
            Enter the new number below. It's encrypted and only used for tax reporting.
          </div>
          <v-row class="my-0">
            <v-text-field
              type="text"
              :label="`${taxIdTypeLabelLong}*`"
              v-model="formattedTaxId"
              :placeholder="taxIdPlaceholder"
              :rules="taxIdDialogRules"
              @input="taxIdDirty = true"
              :maxlength="taxIdMaxLength"
              inputmode="numeric"
              autocomplete="one-time-code"
              required
              outlined
              dense
              class="pa-1"
              @keypress="onTaxIdKeypress"
            >
              <template v-slot:append>
                <v-icon @click="showTaxIdInDialog = !showTaxIdInDialog" tabindex="-1">
                  {{ showTaxIdInDialog ? "mdi-eye" : "mdi-eye-off" }}
                </v-icon>
              </template>
            </v-text-field>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <Button text color="brandPrimary" @click="cancelTaxIdEdit"> Cancel </Button>
          <Button
            color="brandPrimary"
            @click="saveTaxIdEdit"
            :disabled="!isDialogTaxIdValid"
            :loading="savingTaxId"
          >
            Save
          </Button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="text-subtitle-1 font-weight-bold mb-4 text-left">Tax Address</div>

    <v-row class="my-0">
      <v-col cols="12" class="py-0">
        <v-text-field
          :label="fieldLabel('Street Address', addressOptional)"
          v-model="form.taxAddress"
          :rules="addressRules('streetAddress')"
          :disabled="disabled"
          maxlength="255"
          outlined
          dense
          class="pa-1"
        />
      </v-col>
    </v-row>

    <v-row class="my-0">
      <v-col cols="12" class="py-0">
        <v-text-field
          label="Apartment, suite, etc. (optional)"
          v-model="form.taxAddress2"
          :disabled="disabled"
          maxlength="255"
          outlined
          dense
          class="pa-1"
        />
      </v-col>
    </v-row>

    <v-row class="my-0">
      <v-col cols="12" class="py-0">
        <v-text-field
          :label="fieldLabel('City', addressOptional)"
          v-model="form.taxCity"
          :rules="addressRules('city')"
          :disabled="disabled"
          maxlength="255"
          outlined
          dense
          class="pa-1"
        />
      </v-col>
    </v-row>

    <v-row class="my-0">
      <v-col cols="12" sm="6" class="py-0">
        <v-select
          v-if="isUSTaxAddress"
          v-model="form.taxState"
          :items="states"
          :label="fieldLabel('State / Province', addressOptional)"
          item-text="name"
          item-value="abbreviation"
          :required="!addressOptional"
          :rules="addressRules('stateProvince')"
          :disabled="disabled"
          outlined
          dense
          class="pa-1"
          :menu-props="{ bottom: true, offsetY: true }"
        ></v-select>
        <v-text-field
          v-else
          v-model="form.taxState"
          :label="fieldLabel('State / Province', addressOptional)"
          :required="!addressOptional"
          :rules="addressRules('stateProvince')"
          :disabled="disabled"
          outlined
          dense
          class="pa-1"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" class="py-0">
        <v-text-field
          :label="fieldLabel('Zip / Postal Code', addressOptional)"
          v-model="form.taxPostalCode"
          :rules="addressRules('zipPostalCode')"
          :disabled="disabled"
          maxlength="255"
          outlined
          dense
          class="pa-1"
        />
      </v-col>
    </v-row>

    <v-row class="my-0">
      <v-col cols="12" class="py-0">
        <v-select
          :label="fieldLabel('Country', addressOptional)"
          v-model="form.taxCountry"
          :items="countries"
          item-text="name"
          item-value="code"
          :rules="addressRules('country')"
          :disabled="disabled"
          outlined
          dense
          class="pa-1"
        />
      </v-col>
    </v-row>

    <v-row v-if="isOtherCountry" class="my-0">
      <v-col cols="12" class="py-0">
        <v-text-field
          :label="fieldLabel('Country (2 digit code)', addressOptional)"
          v-model="form.taxOtherCountry"
          :rules="addressRules('otherCountry')"
          :disabled="disabled"
          maxlength="2"
          outlined
          dense
          class="pa-1"
        />
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import UserServiceV3 from "@/services/UserServiceV3";
import { states, countries, taxQualification } from "@/shared_data/data";
import Alert from "@/components/generic/Alert.vue";
import Button from "@/components/generic/Button.vue";

// Add a tax id type by adding an entry here; `groups` drives the mask, placeholder,
// max length and the masked read-only value.
const TAX_ID_FORMATS = {
  "US-SSN": {
    abbreviation: "SSN",
    label: "Social Security Number (SSN)",
    labelLong: "Social Security Number",
    groups: [3, 2, 4],
  },
  "US-EIN": {
    abbreviation: "EIN",
    label: "Employer Identification Number (EIN)",
    labelLong: "Employer Identification Number",
    groups: [2, 7],
  },
  "CA-SIN": {
    abbreviation: "SIN",
    label: "Social Insurance Number (SIN)",
    labelLong: "Social Insurance Number",
    groups: [3, 3, 3],
  },
  "CA-BN": {
    abbreviation: "BN",
    label: "Business Number (BN)",
    labelLong: "Business Number",
    groups: [9],
  },
};

const TAX_TYPE_CODE_BY_TAXPAYER_TYPE = {
  US: {
    Individual: "US-SSN",
    Business: "US-EIN",
  },
  CA: {
    Individual: "CA-SIN",
    Business: "CA-BN",
  },
};

const joinGroups = (groups, character) => groups.map((size) => character.repeat(size)).join("-");

// A native password input would hide the dashes too, so the digits are masked manually.
const TAX_ID_MASK_CHARACTER = "\u2022";

// Sentinel option meaning "country not in the list"; the real code is typed by hand.
const OTHER_COUNTRY_CODE = "00";

export default {
  name: "TaxInfoSection",

  components: {
    Alert,
    Button,
  },

  props: {
    hasTaxInfo: {
      type: Boolean,
      default: false,
    },
    savedTaxTypeCode: {
      type: String,
      default: null,
    },
    userProfile: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // Taxpayer type and the legal-name fields it drives belong to the taxpayer, so a
    // third party editing the record can see them but not change them.
    taxpayerFieldsDisabled: {
      type: Boolean,
      default: false,
    },
    addressOptional: {
      type: Boolean,
      default: false,
    },
    // "edit" lets the owner enter/replace the number; "readonly" only reveals whether
    // one is on file (admins editing someone else); "hidden" drops it entirely (new user).
    taxIdMode: {
      type: String,
      default: "edit",
      validator: (value) => ["edit", "readonly", "hidden"].includes(value),
    },
  },

  data() {
    return {
      formValid: false,
      taxIdDirty: false,
      showTaxId: false,
      showTaxIdInDialog: false,
      taxIdEditDialog: false,
      taxIdBeforeEdit: "",
      savingTaxId: false,
      initialForm: null,
      SOLE_PROPRIETOR_SINGLE_MEMBER_LLC: "SOLE_PROPRIETOR_SINGLE_MEMBER_LLC",
      form: {
        taxpayerType: "Individual",
        fullLegalName: "",
        legalBusinessName: "",
        taxTradeName: "",
        taxClassification: "",
        taxId: "",
        taxAddress: "",
        taxAddress2: "",
        taxCity: "",
        taxState: "",
        taxPostalCode: "",
        taxCountry: "",
        taxOtherCountry: "",
      },
      rules: {
        required: (v) => !!v || "This field is required",
        fullLegalName: (v) =>
          !!v ||
          (this.isIndividualTaxpayer
            ? "Full Legal Name is required"
            : "Legal Individual Name is required"),
        legalBusinessName: (v) => !!v || "Legal Business Name is required",
        tradeName: (v) => !!v || "Trade Name is required",
        taxClassification: (v) => !!v || "Tax Classification is required",
        streetAddress: (v) => !!v || "Street Address is required",
        city: (v) => !!v || "City is required",
        stateProvince: (v) => !!v || "State / Province is required",
        zipPostalCode: (v) => !!v || "Zip / Postal Code is required",
        country: (v) => !!v || "Country is required",
        otherCountry: (v) => (!!v && v.length === 2) || "Country code must be exactly 2 letters",
      },
      states,
      countries,
      taxQualification,
    };
  },

  computed: {
    isCanadianTaxpayer() {
      const profile = this.userProfile || {};
      const country =
        profile.businessCountry === OTHER_COUNTRY_CODE
          ? profile.otherBusinessCountry
          : profile.businessCountry;
      return country === "CA";
    },

    isIndividualTaxpayer() {
      return this.form.taxpayerType === "Individual";
    },

    identityDisabled() {
      return this.disabled || this.taxpayerFieldsDisabled;
    },

    isOtherCountry() {
      return this.form.taxCountry === OTHER_COUNTRY_CODE;
    },

    // The Tax Address country (not the taxpayer's business country) decides which
    // State / Province input shows - a US state dropdown, or a free-text field.
    isUSTaxAddress() {
      return !this.form.taxCountry || this.form.taxCountry === "US";
    },

    // Base label with the SSN/EIN abbreviation, e.g. "Social Security Number (SSN)"
    taxIdTypeLabel() {
      return this.taxIdFormat.label;
    },

    // Long-form label without the abbreviation, used in the edit dialog field
    taxIdTypeLabelLong() {
      return this.taxIdFormat.labelLong;
    },

    taxIdAbbreviation() {
      return this.taxIdFormat.abbreviation;
    },

    taxIdFormat() {
      return TAX_ID_FORMATS[this.selectedTaxTypeCode] || TAX_ID_FORMATS["US-SSN"];
    },

    taxIdDigitCount() {
      return this.taxIdFormat.groups.reduce((total, size) => total + size, 0);
    },

    // Derived from the format so a type with a different length needs no extra config.
    TAX_ID_REGEX() {
      return new RegExp(String.raw`^\d{${this.taxIdDigitCount}}$`);
    },

    // Shown in place of the stored number once tax info is on file.
    maskedTaxId() {
      return joinGroups(this.taxIdFormat.groups, "*");
    },

    // Only one of the two tax id fields is ever on screen at a time.
    isTaxIdVisible() {
      return this.taxIdEditDialog ? this.showTaxIdInDialog : this.showTaxId;
    },

    // The stored value stays digits-only; dashes are display only. While hidden the
    // digits are replaced by mask characters so they never reach the DOM.
    formattedTaxId: {
      get() {
        return this.isTaxIdVisible
          ? this.formatTaxId(this.form.taxId)
          : this.maskTaxId(this.form.taxId);
      },
      set(value) {
        const typedDigits = (value || "").replace(/\D/g, "");

        if (this.isTaxIdVisible) {
          this.form.taxId = typedDigits.slice(0, this.taxIdDigitCount);
          return;
        }

        // Mask characters carry no value, so the surviving count of them says how
        // many stored digits the user kept.
        const keptCount = (value || "").split(TAX_ID_MASK_CHARACTER).length - 1;
        this.form.taxId = ((this.form.taxId || "").slice(0, keptCount) + typedDigits).slice(
          0,
          this.taxIdDigitCount
        );
      },
    },

    taxIdPlaceholder() {
      return joinGroups(this.taxIdFormat.groups, "X");
    },

    // Digits plus the separating dashes.
    taxIdMaxLength() {
      return this.taxIdDigitCount + this.taxIdFormat.groups.length - 1;
    },

    selectedTaxTypeCode() {
      const country = this.isCanadianTaxpayer ? "CA" : "US";
      return TAX_TYPE_CODE_BY_TAXPAYER_TYPE[country][this.form.taxpayerType] || "US-SSN";
    },

    TAX_ID_REQUIRED_MSG() {
      return `${this.taxIdTypeLabel} is required`;
    },

    TAX_ID_FORMAT_MSG() {
      return `${this.taxIdAbbreviation} must be exactly ${this.taxIdDigitCount} digits`;
    },

    // A fresh tax id must be entered when there's no tax info yet, or when
    // the taxpayer type was switched to the opposite of what's on file.
    requiresNewTaxId() {
      return (
        this.taxIdMode === "edit" &&
        (!this.hasTaxInfo || this.selectedTaxTypeCode !== this.savedTaxTypeCode)
      );
    },

    // The stored type wins over the selected taxpayer type so a number saved as a
    // SIN is never labelled an SSN.
    readonlyTaxIdFormat() {
      return TAX_ID_FORMATS[this.savedTaxTypeCode] || this.taxIdFormat;
    },

    readonlyTaxIdLabel() {
      return this.readonlyTaxIdFormat.label;
    },

    readonlyTaxIdMessage() {
      return this.hasTaxInfo
        ? `${this.readonlyTaxIdLabel} is provided`
        : `${this.readonlyTaxIdLabel} has not been provided`;
    },

    taxIdRules() {
      return this.requiresNewTaxId
        ? [
            () => !!this.form.taxId || this.TAX_ID_REQUIRED_MSG,
            () => this.TAX_ID_REGEX.test(this.form.taxId || "") || this.TAX_ID_FORMAT_MSG,
          ]
        : [];
    },

    taxIdDialogRules() {
      return [
        () => !!this.form.taxId || this.TAX_ID_REQUIRED_MSG,
        () => this.TAX_ID_REGEX.test(this.form.taxId || "") || this.TAX_ID_FORMAT_MSG,
      ];
    },

    isDialogTaxIdValid() {
      return this.form.taxId && this.TAX_ID_REGEX.test(this.form.taxId);
    },

    isDirty() {
      if (this.taxIdDirty && this.requiresNewTaxId) return true;
      if (!this.initialForm) return false;
      return Object.keys(this.initialForm).some((key) => this.form[key] !== this.initialForm[key]);
    },

    isFormValidManual() {
      const {
        form,
        isIndividualTaxpayer,
        requiresNewTaxId,
        TAX_ID_REGEX,
        SOLE_PROPRIETOR_SINGLE_MEMBER_LLC,
      } = this;
      const isSoleProprietor = form.taxClassification === SOLE_PROPRIETOR_SINGLE_MEMBER_LLC;

      const hasIdentity = isIndividualTaxpayer
        ? !!form.fullLegalName
        : !!form.taxClassification &&
          !!form.legalBusinessName &&
          (isSoleProprietor ? !!form.fullLegalName : !!form.taxTradeName);

      const hasTaxId = !requiresNewTaxId || TAX_ID_REGEX.test(form.taxId || "");

      // "Other" defers the real code to the free-text field, so that must be valid instead.
      const hasCountry =
        form.taxCountry === OTHER_COUNTRY_CODE
          ? (form.taxOtherCountry || "").length === 2
          : !!form.taxCountry;

      const hasAddress =
        this.addressOptional ||
        (!!form.taxAddress &&
          !!form.taxCity &&
          !!form.taxState &&
          !!form.taxPostalCode &&
          hasCountry);

      return hasIdentity && hasTaxId && hasAddress;
    },
  },

  watch: {
    userProfile: {
      handler() {
        this.resetForm();
      },
      immediate: true,
    },

    form: {
      handler() {
        this.emitUpdate();
      },
      deep: true,
    },

    taxIdDirty() {
      this.emitUpdate();
    },

    // requiresNewTaxId (and thus isFormValidManual) depends on these props, so a
    // parent-driven change (e.g. after a successful save) must re-emit validation state.
    hasTaxInfo() {
      this.emitUpdate();
    },

    savedTaxTypeCode() {
      this.emitUpdate();
    },

    // An SSN is not an EIN: a number typed under one taxpayer type must not carry over.
    "form.taxpayerType"() {
      this.form.taxId = "";
      this.taxIdDirty = false;
      this.showTaxId = false;
    },

    // The State field switches between a US dropdown and a free-text field depending on
    // country. A free-text value typed for a non-US country (or a stale value left over
    // from a prior country) may not be one of the US state abbreviations, in which case
    // the dropdown renders blank while form.taxState still holds the old truthy value -
    // passing the required-only rule and leaving Save wrongly enabled. Clear it so the
    // field and its validity agree.
    "form.taxCountry"() {
      if (this.isUSTaxAddress && !this.states.some((s) => s.abbreviation === this.form.taxState)) {
        this.form.taxState = "";
      }
    },

    "form.taxId"(newValue) {
      // Filter out non-numeric characters whenever tax id changes
      if (newValue) {
        const numericOnly = newValue.replace(/\D/g, "");
        if (newValue !== numericOnly) {
          this.$nextTick(() => {
            this.form.taxId = numericOnly;
          });
        }
      }
    },
  },

  methods: {
    // Locked or optional fields carry no required marker.
    fieldLabel(label, optional) {
      return optional ? label : `${label}*`;
    },

    addressRules(ruleName) {
      return this.addressOptional ? [] : [this.rules[ruleName]];
    },

    formatTaxId(value) {
      return this.groupTaxId((value || "").replace(/\D/g, "").slice(0, this.taxIdDigitCount));
    },

    maskTaxId(value) {
      const digitCount = (value || "").replace(/\D/g, "").slice(0, this.taxIdDigitCount).length;
      return this.groupTaxId(TAX_ID_MASK_CHARACTER.repeat(digitCount));
    },

    groupTaxId(characters) {
      const parts = [];
      let offset = 0;
      for (const size of this.taxIdFormat.groups) {
        if (offset >= characters.length) {
          break;
        }
        parts.push(characters.slice(offset, offset + size));
        offset += size;
      }

      return parts.join("-");
    },
    onTaxIdKeypress(event) {
      // Only allow numeric characters (0-9)
      const char = String.fromCharCode(event.keyCode || event.which);
      if (!/^[0-9]$/.test(char)) {
        event.preventDefault();
      }
    },

    openTaxIdEditDialog() {
      this.taxIdBeforeEdit = this.form.taxId;
      this.form.taxId = "";
      this.taxIdEditDialog = true;
      this.showTaxIdInDialog = false;
    },

    cancelTaxIdEdit() {
      this.form.taxId = this.taxIdBeforeEdit;
      this.taxIdEditDialog = false;
      this.taxIdDirty = false;
    },

    async saveTaxIdEdit() {
      if (!this.isDialogTaxIdValid) return;

      this.savingTaxId = true;

      // Update existing tax id via dialog
      const result = await this.updateTaxId();

      this.savingTaxId = false;
      if (result.success) {
        this.taxIdEditDialog = false;
        this.taxIdDirty = false; // Reset after successful save

        // Reset taxId to empty and update initialForm to prevent dirty state
        this.form.taxId = "";
        this.initialForm.taxId = "";

        this.$store.dispatch("showSuccess", {
          message: `${this.taxIdTypeLabelLong} updated successfully`,
        });
      } else {
        this.$store.dispatch("showError", {
          message: `Failed to update ${this.taxIdTypeLabelLong}`,
        });
      }
    },

    emitUpdate() {
      const isValid = this.isFormValidManual;
      const data = {
        taxpayerType: this.form.taxpayerType,
        fullLegalName: this.form.fullLegalName,
        taxAddress: this.form.taxAddress,
        taxAddress2: this.form.taxAddress2,
        taxBusinessName: this.form.legalBusinessName,
        taxTradeName: this.form.taxTradeName,
        taxClassification: this.form.taxClassification,
        taxCity: this.form.taxCity,
        taxState: this.form.taxState,
        taxPostalCode: this.form.taxPostalCode,
        // Resolve the "Other" sentinel to the manually-entered code before saving
        taxCountry: this.isOtherCountry
          ? this.form.taxOtherCountry.toUpperCase()
          : this.form.taxCountry,
        typeCode: this.selectedTaxTypeCode,
      };

      // Include taxId only when a fresh tax id was entered (new user, or type switch) and dirty
      if (this.requiresNewTaxId && this.taxIdDirty) {
        data.taxId = this.form.taxId;
      }
      this.$emit("update", {
        data,
        valid: isValid,
        dirty: this.isDirty,
      });
    },

    async updateTaxId() {
      // Only save Tax ID if it was changed
      if (!this.taxIdDirty) {
        return { success: true };
      }

      try {
        // Update existing tax id (called from edit dialog)
        await UserServiceV3.updateTaxInfo({
          value: this.form.taxId,
          reason: `${this.taxIdTypeLabelLong} updated by user`,
        });

        // Reset dirty flag after successful save
        this.taxIdDirty = false;

        return { success: true };
      } catch (err) {
        const message = err?.response?.data?.message || err?.message || "Failed to update tax info";
        return { success: false, error: message };
      }
    },

    resetForm() {
      const p = this.userProfile || {};
      this.form.taxpayerType = p.taxpayerType || "Individual";
      this.form.fullLegalName = p.fullLegalName || "";
      this.form.taxId = "";
      this.form.taxAddress = p.taxAddress || "";
      this.form.legalBusinessName = p.taxBusinessName || "";
      this.form.taxTradeName = p.taxTradeName || "";
      this.form.taxClassification = p.taxClassification || "";
      this.form.taxAddress2 = p.taxAddress2 || "";
      this.form.taxCity = p.taxCity || "";
      this.form.taxState = p.taxState || "";
      this.form.taxPostalCode = p.taxPostalCode || "";
      // A saved country code outside the dropdown list means it was entered via "Other"
      const knownCountryCode = this.countries.some((c) => c.code === p.taxCountry);
      this.form.taxCountry =
        p.taxCountry && !knownCountryCode ? OTHER_COUNTRY_CODE : p.taxCountry || "";
      this.form.taxOtherCountry = p.taxCountry && !knownCountryCode ? p.taxCountry : "";
      this.taxIdDirty = false;
      this.initialForm = { ...this.form };
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
        this.emitUpdate();
      });
    },
  },
};
</script>
